import { Injectable, inject } from '@angular/core';
import { FilterPreferenceLocksService } from '@clubhub/ignite/services/filter-preferences/filter-preference-locks.service';
import { filterPreferenceLockedNote, productFilterGroupTitles } from '@shared/constants/filter-preference-options';
import { AbstractControl } from '@angular/forms';
import { productFiltersNames } from '@clubhub/constants';
import { IPropertyAndLoan, errorMessages } from '@clubhub/ignite/models/affordability';
import { IApplicants, IMortgageRequirements } from '@clubhub/ignite/models/products';
import { ProductsSearchService } from '@clubhub/ignite/services/products/products-search.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { yesNoOptions } from '@shared/components/formly';
import { FormFieldType, WrapperType } from '@shared/components/formly/formly.config';
import { FormsValidators } from '@shared/components/forms';
import {
  ApplicantCountry,
  BtlType,
  HolidayLetFrequency,
  MortgageLenders,
  MortgageType,
  ProductLengthExtended,
  PropertyType,
  RemortgageType,
  RepaymentMethod,
  TaxStatusBtl,
  UkLocation,
} from '@shared/models';
import { IgniteHelperService, LtvService } from '@shared/services';
import { LenderService } from '@shared/services/lender.service';
import { map } from 'rxjs';

const secondChargeLoanAmountLabel = 'Second charge loan amount';
const loanAmountLabel = 'Loan amount';
const defaultNumberOfApplicants = 1;

@Injectable({
  providedIn: 'root',
})
export class ProductsFormFieldsBtlService {
  private readonly productsSearchService = inject(ProductsSearchService);
  private readonly helperService = inject(IgniteHelperService);
  private readonly filterPreferenceLocks = inject(FilterPreferenceLocksService);
  private readonly lenderService = inject(LenderService);
  private readonly ltvService = inject(LtvService);

  public resetFieldIfEmpty(field: FormlyFieldConfig) {
    if (!field.model) {
      field.formControl?.reset();
    }
  }

  public keyboardEventMarkTouched(field: FormlyFieldConfig, event: KeyboardEvent) {
    const key = event.key;
    if (!['Tab', 'Shift'].includes(key)) {
      field.formControl?.markAsTouched();
    }
  }

  public fields: FormlyFieldConfig[] = [
    {
      key: 'mortgageRequirements',
      wrappers: [WrapperType.FormCard],
      validators: {
        max500PercentLoanValue: {
          expression: FormsValidators.expression(FormsValidators.ltv(500)),
          message: 'Loan amount should not exceed 500% of property value',
          errorPath: 'loanAmount',
        },
        lessThanDeposit: {
          expression: (control: AbstractControl, field: FormlyFieldConfig) => {
            const { loanAmount, propertyValue } = field.form?.getRawValue().mortgageRequirements as IMortgageRequirements;
            const equityLoanValue = control.value.equityLoanValue;
            if (!equityLoanValue || !propertyValue || !loanAmount) {
              return true;
            }

            return equityLoanValue < propertyValue - loanAmount;
          },
          message: 'Equity loan value must be less than deposit',
          errorPath: 'equityLoanValue',
        },
        interestOnlyAmountValidator: {
          expression: (control: AbstractControl) => {

            const repaymentMethodValue = (control as any)['controls']?.repaymentMethod?.value?.value;
            if (repaymentMethodValue === RepaymentMethod.InterestOnlyPartAndPart) {
              const validator = FormsValidators.expression(
                FormsValidators.greaterThanField<IMortgageRequirements>('interestOnlyAmount', 'loanAmount', true),
              );
              return validator(control);
            }

            return true;
          },
          message: errorMessages.interestOnlyLessThanLoanAmount,
          errorPath: 'interestOnlyAmount',
        },
        productTransferInitialDate: this.helperService.getProductTransferInitialPeriodValidation(),
      },
      fieldGroup: [
        {
          type: FormFieldType.Template,
          template: '<span class="text-colour-red-600 fw-bold">*</span> mandatory fields',
        },
        {
          key: 'limitedCompanyPurchase',
          type: 'radio-button',
          props: {
            label: 'Limited company purchase',
            options: yesNoOptions,
          },
        },
        {
          key: 'limitedCompanySpecialPurposeVehicle',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Is the limited company in a special purpose vehicle?',
            options: yesNoOptions,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.limitedCompanyPurchase !== true,
          },
        },
        {
          key: 'portfolioLandlord',
          type: 'radio-button',
          props: {
            label: 'Portfolio landlord',
            options: yesNoOptions,
            helpText: 'Minimum of 4 properties (including this application)',
          },
        },
        {
          key: 'numberOfProperties',
          type: FormFieldType.Number,
          props: {
            required: true,
            label: 'How many properties in the portfolio?',
            min: 4,
          },
          expressions: {
            hide: field => !field.model?.portfolioLandlord,
          },
        },
        {
          key: 'location',
          type: FormFieldType.Select,
          defaultValue: null,
          props: {
            label: 'Property location',
            options: [
              { value: UkLocation.EastMidlands, label: 'East Midlands' },
              { value: UkLocation.EastOfEngland, label: 'East of England' },
              { value: UkLocation.GreaterLondon, label: 'Greater London' },
              { value: UkLocation.NorthEast, label: 'North East' },
              { value: UkLocation.NorthWest, label: 'North West' },
              { value: UkLocation.SouthEast, label: 'South East' },
              { value: UkLocation.SouthWest, label: 'South West' },
              { value: UkLocation.WestMidlands, label: 'West Midlands' },
              { value: UkLocation.YorkshireAndHumberside, label: 'Yorkshire & Humberside' },
              { value: UkLocation.NorthernIreland, label: 'Northern Ireland' },
              { value: UkLocation.Scotland, label: 'Scotland' },
              { value: UkLocation.Wales, label: 'Wales' },
            ],
          },
        },
        {
          key: 'btlType',
          type: 'select',
          defaultValue: BtlType.Standard,
          props: {
            label: 'BTL type',
            options: [
              { label: 'Standard', value: BtlType.Standard },
              { label: 'HMO', value: BtlType.HMO },
              { label: 'Holiday let', value: BtlType.HolidayLet },
              { label: 'MUFB', value: BtlType.MUFB },
            ],
            required: true,
          },
        },
        {
          key: 'numberOfBedrooms',
          type: FormFieldType.Number,
          props: {
            required: true,
            label: 'Number of bedrooms',
            min: 2,
            max: 20,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.btlType !== BtlType.HMO,
          },
        },
        {
          key: 'numberOfBlocks',
          type: FormFieldType.Number,
          props: {
            required: true,
            label: 'Number of units',
            min: 2,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.btlType !== BtlType.MUFB,
          },
        },
        {
          key: 'propertySelfContainedFlats',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Does the property have self contained flats?',
            options: yesNoOptions,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.btlType !== BtlType.MUFB,
          },
        },
        {
          key: 'newBuild',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'New build',
            options: yesNoOptions,
          },
        },
        {
          key: 'propertyType',
          type: FormFieldType.RadioButtons,
          defaultValue: PropertyType.House,
          props: {
            label: 'Property type',
            options: [
              { label: 'House', value: PropertyType.House },
              { label: 'Flat', value: PropertyType.Flat },
            ],
            required: true,
          },
          expressions: {
            hide: field => !field.model?.newBuild,
          },
        },
        {
          key: 'mortgageType',
          type: 'radio-button',
          props: {
            label: 'Mortgage type',
            options: this.getMortgageTypeOptions(),
            required: true,
          },
        },
        {
          key: 'remortgageType',
          type: 'select',
          props: {
            label: 'Remortgage type',
            allowNull: true,
            options: [
              { label: 'Let to buy', value: RemortgageType.LetToBuy },
              { label: 'Like for like', value: RemortgageType.LikeForLike },
              { label: 'Capital raising', value: RemortgageType.CapitalRaising },
            ],
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.mortgageType !== MortgageType.Remortgage,
          },
        },
        {
          key: 'firstTimeBuyer',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'First time buyer',
            options: yesNoOptions,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.mortgageType !== MortgageType.Purchase,
          },
        },
        {
          key: 'firstTimeLandlord',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'First time landlord',
            options: yesNoOptions,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.mortgageType !== MortgageType.Purchase,
          },
        },
        {
          key: 'ownerOccupier',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'Owner occupier',
            options: yesNoOptions,
          },
          expressions: {
            hide: ({ model }: any) => model?.limitedCompanyPurchase !== false && model?.firstTimeBuyer !== false,
          },
        },
        ...this.mortgageLenderFields(),
        {
          key: 'propertyValue',
          type: FormFieldType.Currency,
          props: {
            label: 'Property value',
            required: true,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
          expressions: {
            'props.readonly': () => this.productsSearchService.propertyValueDisabled,
          },
        },
        {
          key: 'loanAmount',
          type: FormFieldType.Currency,
          props: {
            required: true,
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
          expressions: {
            'props.readonly': () => this.productsSearchService.loanAmountDisabled,
            'props.label': field => {
              return (field.model as IPropertyAndLoan)?.mortgageType === MortgageType.SecondCharge
                ? secondChargeLoanAmountLabel
                : loanAmountLabel;
            },
          },
        },
        {
          key: 'outstandingBalanceCurrentMortgage',
          type: FormFieldType.Currency,
          props: {
            required: true,
            label: 'Outstanding balance on the current mortgage',
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThanOnTouched(0)],
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.mortgageType !== MortgageType.SecondCharge,
            'props.readonly': () => this.productsSearchService.loanAmountDisabled,
          },
        },
        {
          key: 'ltv',
          wrappers: [WrapperType.HtmlWrapper],
          expressions: {
            'props.html': field => {
              const ltv = this.ltvService.calculateLtv((field.parent?.model ?? {}) as IMortgageRequirements);

              if (!ltv) {
                return '';
              }
              return `
                <div class="ltv-display">
                  LTV ${ltv}%
                </div>
              `;
            },
          },
        },
        {
          key: 'repaymentMethod',
          props: {
            label: 'Repayment method',
            required: true,
            suppressErrors: true,
          },
          fieldGroup: [
            {
              key: 'value',
              type: 'select',
              props: {
                label: 'Repayment method',
                hideLabel: true,
                required: true,
                nested: true,
                allowNull: true,
                options: [
                  { label: 'Capital and interest', value: RepaymentMethod.CapitalAndInterest },
                  { label: 'Interest only', value: RepaymentMethod.InterestOnly },
                  { label: 'Interest only part and part', value: RepaymentMethod.InterestOnlyPartAndPart },
                ],
                collapseMargin: true,
              },
            },
          ],
        },
        {
          key: 'interestOnlyAmount',
          type: FormFieldType.Currency,
          props: {
            label: 'Amount on interest only',
            required: true,
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          expressions: {
            hide: field =>
              (field.parent?.model as IMortgageRequirements)?.repaymentMethod?.value !== RepaymentMethod.InterestOnlyPartAndPart,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'mortgageTerm',
          fieldGroupClassName: 'row',
          wrappers: ['horizontal-field'],
          props: {
            label: 'Mortgage term',
            suppressErrors: true,
            required: true,
          },
          fieldGroup: [
            {
              key: 'years',
              type: 'number',
              className: 'col',
              props: {
                label: 'Years',
                nested: true,
                required: true,
                min: 0,
                max: 40,
                groupedField: true,
                collapseMargin: true,
              },
              validation: [FormsValidators.integer],
            },
            {
              key: 'months',
              type: 'number',
              className: 'col',
              props: {
                label: 'Months',
                min: 0,
                max: 11,
                groupedField: true,
                collapseMargin: true,
              },
              validators: {
                validation: [FormsValidators.integer],
              },
            },
          ],
          validators: {
            max40Years: {
              expression: (control: AbstractControl) => {
                const { years, months } = control.value ?? {};
                if (!years) {
                  return true;
                }
                return 12 * years + (months ?? 0) <= 40 * 12;
              },
              message: 'Mortgage term has a maximum of 40 years',
            },
          },
        },
        {
          key: 'holidayLetIncome',
          expressions: {
            hide: field => (field.parent?.model as IPropertyAndLoan)?.btlType !== BtlType.HolidayLet,
          },
          props: {
            suppressErrors: true,
          },
          fieldGroup: [
            {
              template: `
                <h2 class="fw-light mt-4 mb-3">Holiday let income</h2>
              `,
            },
            {
              key: 'holidayLetIncomeLow',
              type: FormFieldType.Currency,
              defaultValue: 0,
              props: {
                label: 'Low season rental amount received / expected',
              },
            },
            {
              key: 'holidayLetIncomeMedium',
              type: FormFieldType.Currency,
              defaultValue: 0,
              props: {
                label: 'Mid season rental amount received / expected',
              },
            },
            {
              key: 'holidayLetIncomeHigh',
              type: FormFieldType.Currency,
              props: {
                label: 'High season rental amount received / expected',
              },
            },
            {
              key: 'holidayLetIncomeFrequency',
              type: FormFieldType.Select,
              defaultValue: null,
              props: {
                options: this.helperService.buildSelectOptionsFromEnum(HolidayLetFrequency),
                label: 'Payment frequency',
                selectClass: 'mb-5',
              },
            },
          ],
        },
        {
          key: 'rentalAmountReceivedExpected',
          type: FormFieldType.Currency,
          props: {
            label: 'Monthly rental amount',
            helpText: 'Expected or received',
          },
        },
        {
          key: 'productLengths',
          type: FormFieldType.MultiSelect,
          expressions: {
            // Locked by the broker's firm: shown, populated, but not theirs to change. Readonly rather than
            // disabled - Formly implements disabled by disabling the FormControl, and Angular then leaves it out
            // of form.value, so the seeded value would never reach the search request.
            'props.readonly': () =>
              this.filterPreferenceLocks.isLocked('product', productFilterGroupTitles.initialProductPeriod),
            // Paired with the readonly above: a field that simply stops responding reads as a bug, so it has to
            // say who locked it. Both read the same lock, so the note cannot appear without the lock or vice versa.
            'props.lockedNote': () =>
              this.filterPreferenceLocks.isLocked('product', productFilterGroupTitles.initialProductPeriod)
                ? filterPreferenceLockedNote
                : null,
          },
          defaultValue: [],
          props: {
            label: productFiltersNames.initialProductPeriod,
            options: this.helperService.productLengthExtendedOptions(),
            required: false,
          },
        },
        {
          key: 'customProductLength',
          fieldGroupClassName: 'row',
          wrappers: [WrapperType.HorizontalField],
          props: {
            suppressErrors: true,
          },
          expressions: {
            hide: field => !(field.parent?.model as IMortgageRequirements)?.productLengths?.includes(ProductLengthExtended.Custom),
          },
          fieldGroup: [
            {
              key: 'from',
              type: 'number',
              className: 'col',
              name: 'From',
              resetOnHide: true,
              wrappers: [WrapperType.HorizontalField],
              props: {
                min: 0,
                label: 'From',
                addonEnd: 'months',
                addonEndClass: 'fw-light',
                groupedField: true,
                collapseMargin: true,
                required: true,
              },
              validation: [FormsValidators.integer],
            },
            {
              key: 'to',
              type: 'number',
              className: 'col',
              resetOnHide: true,
              wrappers: [WrapperType.HorizontalField],
              props: {
                min: 0,
                label: 'To',
                addonEnd: 'months',
                addonEndClass: 'fw-light',
                groupedField: true,
                collapseMargin: true,
                required: true,
              },
              validators: {
                validation: [FormsValidators.integer],
              },
            },
          ],
        },
        {
          key: 'rateTypes',
          type: FormFieldType.MultiSelect,
          expressions: {
            // Locked by the broker's firm: shown, populated, but not theirs to change. Readonly rather than
            // disabled - Formly implements disabled by disabling the FormControl, and Angular then leaves it out
            // of form.value, so the seeded value would never reach the search request.
            'props.readonly': () =>
              this.filterPreferenceLocks.isLocked('product', productFilterGroupTitles.rateType),
            // Paired with the readonly above: a field that simply stops responding reads as a bug, so it has to
            // say who locked it. Both read the same lock, so the note cannot appear without the lock or vice versa.
            'props.lockedNote': () =>
              this.filterPreferenceLocks.isLocked('product', productFilterGroupTitles.rateType)
                ? filterPreferenceLockedNote
                : null,
          },
          defaultValue: [],
          props: {
            label: 'Rate types',
            options: this.helperService.rateTypesOptions,
            required: false,
          },
        },
        {
          key: 'applicants',
          wrappers: [WrapperType.EmptyWrapper],
          fieldGroup: [
            {
              key: 'numberOfApplicants',
              type: FormFieldType.RadioButtons,
              defaultValue: defaultNumberOfApplicants,
              props: {
                label: 'Number of applicants',
                options: [
                  { value: 1, label: '1' },
                  { value: 2, label: '2' },
                  { value: 3, label: '3' },
                  { value: 4, label: '4' },
                ],
                required: false,
                change: (field: FormlyFieldConfig) => {
                  const numberOfApplicants = +field.formControl?.value;
                  this.onNumberOfApplicantsChange(numberOfApplicants);
                },
              },
              hooks: {
                onInit: field => {
                  const numberOfApplicants = field.formControl?.value ?? defaultNumberOfApplicants;
                  field.formControl?.setValue(numberOfApplicants);
                  this.onNumberOfApplicantsChange(numberOfApplicants);
                },
              },
            },
            {
              key: 'applicantAges',
              type: FormFieldType.Repeating,
              props: {
                suppressErrors: true,
                id: 'applicantAges',
              },
              fieldArray: {
                wrappers: [WrapperType.NoWrappers],
                props: {
                  suppressErrors: true,
                },
                fieldGroup: [
                  {
                    expressions: {
                      template: field => {
                        return `
                          <h2 class="fw-light mt-4 mb-3">Applicant ${field.model.applicantNumber}</h2>
                        `;
                      },
                    },
                  },
                  {
                    key: 'dateOfBirth',
                    type: FormFieldType.Date,
                    props: {
                      label: 'Date of birth',
                    },
                    validators: {
                      validation: [
                        FormsValidators.dateOrEmpty,
                        FormsValidators.atLeast18YearsOrEmpty,
                        FormsValidators.dateNotBefore126YearsAgo,
                      ],
                    },
                  },
                  {
                    key: 'countryResident',
                    type: FormFieldType.Select,
                    defaultValue: null,
                    props: {
                      label: 'Current country of residence',
                      options: this.helperService.countryResidentTypeOptions,
                    },
                  },
                  {
                    key: 'grossAnnualIncome',
                    type: FormFieldType.Currency,
                    props: {
                      label: 'Gross annual income',
                      helpText: 'Include total of all income before tax',
                    },
                  },
                  {
                    key: 'taxStatusBtl',
                    type: FormFieldType.Select,
                    defaultValue: null,
                    props: {
                      label: 'Tax status',
                    },
                    validators: {
                      validation: [FormsValidators.isNullOrEnumMember(TaxStatusBtl)],
                    },
                    expressions: {
                      'props.options': field => {
                        const countryResident = field.model?.countryResident;
                        if (countryResident === ApplicantCountry.Scotland) {
                          return this.helperService
                            .buildSelectOptionsFromEnum(TaxStatusBtl)
                            .filter(option => option.value !== TaxStatusBtl.Additional);
                        }
                        return this.helperService
                          .buildSelectOptionsFromEnum(TaxStatusBtl)
                          .filter(
                            option =>
                              option.value !== TaxStatusBtl.Top &&
                              option.value !== TaxStatusBtl.Starter &&
                              option.value !== TaxStatusBtl.Intermediate,
                          );
                      },
                      hide: field => field.model?.countryResident === null,
                    },
                  },
                ],
              },
            },
          ],
        },
      ],
    },
    {
      key: 'getResultsBtn',
      type: 'buttons',
      props: {
        numColumns: 6,
        buttons: [{ label: 'Search', type: 'submit', className: 'btn-colour-blue-600' }],
      },
    },
  ];

  private onNumberOfApplicantsChange(numberOfApplicants: number) {
    if (!this.productsSearchService.model) {
      this.productsSearchService.updateModel();
    }

    const model = this.productsSearchService.model;

    if (model && !model?.mortgageRequirements?.applicants) {
      model.mortgageRequirements.applicants = {} as IApplicants;
    }

    if (model?.mortgageRequirements.applicants && !model.mortgageRequirements.applicants.applicantAges) {
      model.mortgageRequirements.applicants.applicantAges = this.helperService.generateEmptyApplicantAges(numberOfApplicants);
      this.productsSearchService.updateModel(this.productsSearchService.model);
      return;
    }

    const applicantAges = model?.mortgageRequirements.applicants?.applicantAges;

    if (applicantAges && applicantAges.length > numberOfApplicants && model?.mortgageRequirements.applicants) {
      model.mortgageRequirements.applicants.applicantAges = applicantAges?.slice(0, numberOfApplicants);
    } else if (applicantAges && applicantAges.length < numberOfApplicants) {
      const emptyApplicantAges = this.helperService.generateEmptyApplicantAges(
        numberOfApplicants - applicantAges.length,
        applicantAges.length,
      );
      model.mortgageRequirements.applicants?.applicantAges?.push(...emptyApplicantAges);
    }

    this.productsSearchService.updateModel(this.productsSearchService.model);
  }

  private getMortgageTypeOptions() {
    return [
      { label: 'Purchase', value: MortgageType.Purchase },
      { label: 'Remortgage', value: MortgageType.Remortgage },
      { label: 'Second charge', value: MortgageType.SecondCharge },
    ];
  }

  private mortgageLenderFields(): FormlyFieldConfig[] {
    const mortgageLenderHidden = (field: any) => {
      return (
        field?.model?.mortgageType !== MortgageType.Remortgage &&
        (field?.model?.mortgageType !== MortgageType.Purchase || field?.model?.firstTimeBuyer !== false)
      );
    };

    const initialPeriodHidden = (field: any) => field?.model?.mortgageType !== MortgageType.Remortgage;

    return [
      {
        key: 'mortgageLender',
        wrappers: [WrapperType.NoWrappers],
        props: {
          suppressErrors: true,
        },
        fieldGroup: [
          {
            key: 'value',
            type: FormFieldType.Select,
            props: {
              label: 'Who is the current mortgage Lender?',
              allowNull: true,
            },
            expressions: {
              hide: field => mortgageLenderHidden(field.parent?.parent),
              'props.options': this.lenderService
                .getUnarchivedLendersNames(this.helperService.getBtlLendingType())
                .pipe(map(lenderNames => this.helperService.getMortgageLendersList(lenderNames))),
            },
          },
        ],
      },
      {
        key: 'currentMortgageLender',
        type: FormFieldType.Input,
        expressions: {
          hide: field => {
            if (mortgageLenderHidden(field)) {
              return true;
            }

            const mortgageLenderValue = field.model?.mortgageLender?.value;
            return !mortgageLenderValue || mortgageLenderValue !== MortgageLenders.Other;
          },
        },
      },
      {
        key: 'productTransferInitialDate',
        type: FormFieldType.Date,
        props: {
          label: 'What is the end date for the Initial period?',
        },
        validators: {
          validation: [FormsValidators.initialPeriodDate],
        },
        expressions: {
          hide: field => {
            if (initialPeriodHidden(field)) {
              return true;
            }

            return field.model?.mortgageLender?.value === MortgageLenders.Other;
          },
        },
      },
    ];
  }
}
